import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { ApiService } from './api.service';
import { EntityService } from './entity.service';
import { AuditSession, User } from '../models';
import { TempStorageService } from './temp-storage.service';
import { MODULE_DEFAULT } from '../global';
import { HttpErrorResponse } from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';

export const ROLES = {
  ADMINISTRADOR: 'ADMINISTRADOR',
  ROLE_ADMINISTRADOR: 'ROLE_ADMINISTRADOR',
  ROLE_SUPERVISOR: 'ROLE_SUPERVISOR',
  ROLE_CONSULTOR: 'ROLE_CONSULTOR',
  EXTERNAL_ROLE: 'EXTERNO',
  ROLE_EXTERNAL_USER: 'ROLE_EXTERNAL_USER',
};

@Injectable()
export class AuthService extends TempStorageService {
  private loggedIn = new BehaviorSubject<boolean>(false);
  private userIdentity = new BehaviorSubject<any>({});
  constructor(
    public apiService: ApiService,
    private entityService: EntityService,
    private readonly msalSvc: MsalService,
  ) {
    super();
    this.loggedIn.next(this.isAuthenticated());
    this.userIdentity.next(this.getUser());
  }

  isRoleExternalUser(): boolean {
    const user: User = this.getUser();
    const roleExternalUserFound: any = user.roles && user.roles.length > 0 && user.roles.find(role => role === ROLES.ROLE_EXTERNAL_USER);
    return roleExternalUserFound;
  }

  get isLoggedIn() {
    return this.loggedIn.asObservable();
  }

  get user() {
    return this.userIdentity.asObservable();
  }

  async login(body: any): Promise<User|any> {
    try {
      const headers = { headers: { 'app-name': 'WEB' } };
      const user: User = await this.apiService
      .post(this.apiService.login, body, headers)
      .toPromise();
      user.entity.entityCode = user.entity.entityId.toString();
      if (user.visibleModules.length) {
        user.visibleModules = user.visibleModules
        // .filter(i => !(i.moduleName === 'Picking' && user.entity.company.code === 'SPSA'))
        .concat(MODULE_DEFAULT);
      }
      this.setSession({user});
      if (
        user.visibleModules.length === 1 &&
        user.visibleModules[0].moduleIdentifier === 'ventas'
      ) {
        const stores = await this.entityService.getStores();
        this.setSession({user, stores});
      }
      this.loggedIn.next(true);
      this.userIdentity.next(user);
      return user;
    } catch (error) {
      throw error;
    }
  }

  changePassword(request): Observable<any> {
    return this.apiService
      .put(`${this.apiService.changePassword}`, request)
      .pipe(map(item => item));
  }

  getCompanies(): Observable<any> {
    return this.apiService.get(this.apiService.apiAuth + '/companies');
  }

  setUserIp() {
    const userIPStorage = localStorage.getItem(environment.userIP);
    return userIPStorage
      ? of(userIPStorage)
      : this.apiService
        .get(`${environment.apiExternalIPapi}/json`)
        .pipe(tap((res) => {
          localStorage.setItem(environment.userIP, res.ip);
        }));
  }

  getUserIPStorage(): string {
    return localStorage.getItem(environment.userIP);
  }

  logout() {
    this.loggedIn.next(false);
    localStorage.removeItem(environment.sessionAuth);
    localStorage.removeItem(environment.config_enable_repactcha.googlereCAPTCHA);
    localStorage.removeItem(environment.config_enable_repactcha.reCAPTCHA);
    sessionStorage.removeItem(environment.sessionData);
    this.removeItems();
  }

  logoutAndSaveAudit() {
    //Se obtiene el usuario y companyCode antes de limpiar el cache
    const session = JSON.parse(localStorage.getItem(environment.sessionAuth));
    if (session && session.user) {
      const companyCode = session.user.entity.company.code;
      const userName = session.user.username;
      const auditSession: AuditSession = {
        username: userName,
        companyCode: companyCode,
        action: 'LOGOUT',
        response: 'SUCCES'
      };
      this.logAuditSession(auditSession);
    }
    this.loggedIn.next(false);
    localStorage.removeItem(environment.sessionAuth);
    localStorage.removeItem(environment.config_enable_repactcha.googlereCAPTCHA);
    localStorage.removeItem(environment.config_enable_repactcha.reCAPTCHA);
    sessionStorage.removeItem(environment.sessionData);
    this.removeItems();
  }
  /**
   * Gets user
   * @returns user
   */
  getUser(): User {
    if (localStorage.getItem(environment.sessionAuth)) {
      const session = JSON.parse(localStorage.getItem(environment.sessionAuth));
      return session.user;
    } else {
      this.logout();
      return null;
    }
  }

  getCompanyCode(): string{
    return this.getUser().entity.company.code;
  }

  /**
   * Verificar arreglo de roles con los roles que tiene el usuario
   * si uno de los roles son correctos retorna true, caso contrario false
   * @param roles: any[]
   */
  verifyRoles(roles: any[]) {
    const user: User = this.getUser();
    for (const rol of user.roles) {
      if (roles.contains(rol)) {
        return true;
      }
    }
    return false;
  }
  /**
   * Sets session
   * @param session: any
   */
  private setSession(session: any): void {
    session.expires_in = 1000 * 60 * 60 * 10 + new Date().getTime();
    localStorage.setItem(environment.sessionAuth, JSON.stringify(session));
  }
  /**
   * Determines whether authenticated is
   * @returns true if authenticated
   */
  public isAuthenticated(): boolean {
    if (localStorage.getItem(environment.sessionAuth)) {
      const session = JSON.parse(localStorage.getItem(environment.sessionAuth));
      if (new Date().getTime() < session.expires_in) {
        return true;
      } else {
        this.logout();
        return false;
      }
    } else {
      return false;
    }
  }
  /**
   * Removes items picking
   */
  private removeItems() {
    // localStorege por usuario
    this.removeListStorage(Object.keys(localStorage));
    this.removeListSession(Object.keys(sessionStorage));
  }

  async loginMsal(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.msalSvc.loginPopup()
        .subscribe(async (response: AuthenticationResult) => {
          const responseUser: any[] = await this.loginByEmail(response.account.username);
          const users: User[] = responseUser.map(element => {
               return element.body;
          });
          if (users && users.length > 0) {
            let user : any = users[0];
            this.applyUser(user, users);
          } else {
            this.apiService.formatErrors(new HttpErrorResponse({ error: {message:"No se encuentra habilitado o no posee ningún usuario asignado", statusText:"Unauthorized"}, statusText: "No se encuentra habilitado o no posee ningún usuario asignado", status: 401 }));
          }
          return resolve(users);
          },
          (error) => {
            reject(error);
          }
        );
    });
  }

  async applyUser(user: User, users: User[]) {
    user.entity.entityCode = user.entity.entityId.toString();
    if (user.visibleModules.length) {
      user.visibleModules = user.visibleModules.concat(MODULE_DEFAULT);
    }
    this.setSession({user});
    if (user.visibleModules.length === 1 && user.visibleModules[0].moduleIdentifier === 'ventas') {
      const stores = await this.entityService.getStores();
      this.setSession({user, stores});
    }
    this.loggedIn.next(true);
    this.userIdentity.next(user);
    this.setMultiSession(users);
 }

 setMultiSession(users: User[]) {
    localStorage.setItem(environment.sessionMultiData, JSON.stringify(users));
 }

 getMultiSession() {
    return localStorage.getItem(environment.sessionMultiData);
 }

 async loginByEmail(email: string) {
    return this.apiService.postWithoutInterceptor(this.apiService.loginByEmail, { email: email }).toPromise();
 }

 logAuditSession(auditSession: AuditSession){
   let userIPStorage = localStorage.getItem(environment.userIP);
   auditSession.ip = userIPStorage;
   if (auditSession.ip  == null) {
     this.setUserIp().subscribe(res => {
       auditSession.ip  = res.ip
       this.apiService.post(this.apiService.apiAuth + '/audit/logout', auditSession).subscribe(response => { });
     });
   } else {
     this.apiService.post(this.apiService.apiAuth + '/audit/logout', auditSession).subscribe(response => { });
   }
  }

  isExternalRole(): boolean {
    const user: User = this.getUser();
    const isOnlyConstantRole: boolean = user.roles && user.roles.length === 1 && user.roles[0] === ROLES.EXTERNAL_ROLE;
    return isOnlyConstantRole;
  }
}
